import {
  CartIcon,
  createWebviewScreen,
  SearchIcon,
} from 'shared/src/component';

/**
 * 경로 + 타이틀만 다른 웹뷰 전용 화면들. 추가할 때는
 * 1. `shared/src/webRoutePaths.ts` 의 `WEB_ROUTE_PATHS` 에 경로 추가
 * 2. `WEBVIEW_WRAPPED_NATIVE_SCREENS` 에 화면명 추가 (딥링크로 감싸져 와도 전용 화면으로)
 * 3. 여기에 한 줄 + `Navigator.tsx` 의 `ScreensPageScreens` 에 등록
 *
 * 동적 세그먼트가 있는 경로도 여기서 만든다(팩토리가 채운다). 화면마다 다른 동작이 필요한
 * 경우(웹 타이틀 수신 등)만 개별 파일로 둔다 — EventDetail 참고.
 */
export const HelpScreen = createWebviewScreen({
  screen: 'Help',
  title: '고객센터',
  showHeader: true,
  rightIcons: [<CartIcon />],
});

/** 1:1 문의 (`/help/inquiry/new` 작성, `/help/inquiry/:sno` 상세) */
export const HelpInquiryScreen = createWebviewScreen({
  screen: 'HelpInquiry',
  title: '1:1 문의',
});

export const CouponsScreen = createWebviewScreen({
  screen: 'Coupons',
  title: '쿠폰',
});

export const PointsScreen = createWebviewScreen({
  screen: 'Points',
  title: '포인트',
});

export const MembershipScreen = createWebviewScreen({
  screen: 'Membership',
  title: '멤버십 혜택 보기',
  showHeader: true,
  rightIcons: [<CartIcon />],
});

export const NotificationsScreen = createWebviewScreen({
  screen: 'Notifications',
  title: '알림',
});

export const BenefitsScreen = createWebviewScreen({
  screen: 'Benefits',
  title: '혜택',
});

/* 각자 자체 타이틀바를 그리는 지면들이라 showHeader 는 켜지 않는다(켜면 헤더 두 개). */

export const CommunityTalkHomeScreen = createWebviewScreen({
  screen: 'CommunityTalkHome',
  title: '커뮤니티',
});

/** 커뮤니티 옛 경로(`/fun/talk`). 서버 307 로 `/community/talk/home` 에 착지한다. */
export const FunTalkScreen = createWebviewScreen({
  screen: 'FunTalk',
  title: '커뮤니티',
});

export const ChatBotScreen = createWebviewScreen({
  screen: 'ChatBot',
  title: 'AI 채팅',
});

export const FortuneHomeScreen = createWebviewScreen({
  screen: 'FortuneHome',
  title: '운세',
});

/** 운세 옛 경로(`/life/fortune/daily`). 서버 308 두 번으로 `/fortune/home` 에 착지한다. */
export const LifeFortuneDailyScreen = createWebviewScreen({
  screen: 'LifeFortuneDaily',
  title: '운세',
});

/**
 * ⚠️ 아래 둘은 stag-4 에 없고 prod 에만 있다(스테이징 실측 불가). 타이틀도 페이지를 열어
 * 확인하지 못해 경로 의미로 정했다 — 실제 지면을 보고 어긋나면 고칠 것.
 */
export const ContentStyleHomeScreen = createWebviewScreen({
  screen: 'ContentStyleHome',
  title: '스타일',
});

export const NovelHomeScreen = createWebviewScreen({
  screen: 'NovelHome',
  title: '웹소설',
});

/**
 * 매거진(`/style`). 페이지가 자체 `WebNavigationBar title="매거진"` 을 그리므로
 * showHeader 는 켜지 않는다(켜면 헤더 두 개).
 */
export const StyleScreen = createWebviewScreen({
  screen: 'Style',
  title: '매거진',
});

export const PlaceGoodsDetailScreen = createWebviewScreen({
  screen: 'PlaceGoodsDetail',
  title: '상품 상세',
});

/**
 * 온보딩(`/onboarding/today`).
 *
 * 전체화면 플로우라 페이지가 타이틀바를 그리지 않고 뒤로가기도 막는다. 그래서 네이티브 헤더도
 * 띄우지 않는다(showHeader 기본 false). 여기 title 은 문서 타이틀 용도다.
 */
export const TodayOnboardingScreen = createWebviewScreen({
  screen: 'TodayOnboarding',
  title: '온보딩',
});

/* 플레이스 지도 / 검색. 페이지가 자체 타이틀바를 그려 showHeader 는 켜지 않는다. */

/**
 * 플레이스 카테고리 상세(`/place/list/:categorySno`).
 *
 * 페이지가 자체 타이틀바를 그리고 그 문구가 **카테고리 이름(동적)** 이라
 * (`screenName: PLACE_CATEGORY`) showHeader 는 켜지 않고, 여기 title 은 문서 타이틀 용도로
 * 고정 문구를 쓴다.
 */
export const PlaceCategoryListScreen = createWebviewScreen({
  screen: 'PlaceCategoryList',
  title: '플레이스',
});

export const PlaceMapScreen = createWebviewScreen({
  screen: 'PlaceMap',
  title: '플레이스 지도',
});

export const PlaceSearchScreen = createWebviewScreen({
  screen: 'PlaceSearch',
  title: '검색',
});

export const PaymentCheckoutScreen = createWebviewScreen({
  screen: 'PaymentCheckout',
  title: '결제',
});

/* 결제 결과 — PG 가 돌려보내는 지면. `?sno=` 는 path 세그먼트가 아니라 웹뷰 URL 쿼리로 승계된다. */

export const PaymentSuccessScreen = createWebviewScreen({
  screen: 'PaymentSuccess',
  title: '주문 완료',
  showHeader: true,
  rightIcons: [<CartIcon />],
});

export const PaymentFailScreen = createWebviewScreen({
  screen: 'PaymentFail',
  title: '결제 실패',
  showHeader: true,
  rightIcons: [<CartIcon />],
});

export const PlaceReservationListScreen = createWebviewScreen({
  screen: 'PlaceReservationList',
  title: '예약 내역',
});

export const MarketInfoScreen = createWebviewScreen({
  screen: 'MarketInfo',
  title: '마켓 정보',
});

/* 무물라이브 — 목록(`/mumul-live/profiles`)과 LIVE 채널 상세(`/mumul-live/profiles/:sno`) */

export const MumulLiveProfilesScreen = createWebviewScreen({
  screen: 'MumulLiveProfiles',
  title: 'LIVE 채널',
});

export const MumulLiveProfileSnoScreen = createWebviewScreen({
  screen: 'MumulLiveProfileSno',
  title: 'LIVE 채널',
});

/**
 * 상태별 주문 목록(`/orders/status/:status`).
 *
 * 타이틀은 상태마다 다르지만(배송중 / 배송완료 / 취소·반품) 콘텐츠가 자체 `WebNavigationBar` 로
 * 그리므로 showHeader 는 켜지 않는다. 여기 title 은 문서 타이틀 용도라 상태 전체를 덮는 문구로 둔다.
 */
export const OrderStatusScreen = createWebviewScreen({
  screen: 'OrderStatus',
  title: '주문 상태',
  showHeader: true,
  rightIcons: [<CartIcon />],
});

/* 주문 상세에서 파생되는 웹뷰 화면들 (`/orders/:orderSno/...`) */

export const OrderCancellationsScreen = createWebviewScreen({
  screen: 'OrderCancellations',
  title: '주문 취소',
});

export const OrderCancellationsDetailScreen = createWebviewScreen({
  screen: 'OrderCancellationsDetail',
  title: '취소 상세',
  showHeader: true,
  rightIcons: [<SearchIcon />, <CartIcon />],
});

export const OrderExchangeScreen = createWebviewScreen({
  screen: 'OrderExchange',
  title: '교환요청',
});

/** 교환요청 안의 마켓(판매자) 교환 흐름. 콘텐츠가 자체 타이틀바를 `교환요청` 으로 그린다. */
export const OrderExchangeSellersScreen = createWebviewScreen({
  screen: 'OrderExchangeSellers',
  title: '교환요청',
});

export const OrderReturnScreen = createWebviewScreen({
  screen: 'OrderReturn',
  title: '반품요청',
});

/** 반품 상세(`/orders/:orderSno/return/:orderReturnSno`). 스테이징 `<title>` 이 `반품상세` 다. */
export const OrderReturnDetailScreen = createWebviewScreen({
  screen: 'OrderReturnDetail',
  title: '반품상세',
});

export const OrderSalesInvoiceScreen = createWebviewScreen({
  screen: 'OrderSalesInvoice',
  title: '거래명세서',
});

export const OrderDeliveryPostScreen = createWebviewScreen({
  screen: 'OrderDeliveryPost',
  title: '배송 정보',
});

/** 주문 아이템 단위 배송조회. 스테이징 `<title>` 이 `배송조회` 다. */
export const OrderDeliveryPostOrderItemScreen = createWebviewScreen({
  screen: 'OrderDeliveryPostOrderItem',
  title: '배송조회',
  showHeader: true,
  rightIcons: [<CartIcon />],
});

/**
 * 쿠폰/이벤트 목록(`/events`). 상세는 웹 타이틀을 받아 헤더에 반영해야 해서 별도 파일
 * (`EventDetail`)로 두지만, 목록은 경로+타이틀만 달라 팩토리로 충분하다.
 * 타이틀은 스테이징 응답의 `<title>`(쿠폰 / 이벤트)을 그대로 쓴다.
 */
export const EventListScreen = createWebviewScreen({
  screen: 'EventList',
  title: '쿠폰 / 이벤트',
  showHeader: true,
});

/**
 * 기획전 세그먼트(구좌) 지면(`/events/:eventId/segments/:segmentSno`).
 *
 * 상세(`EventDetail`)는 웹 타이틀을 받아 네이티브 헤더에 반영하는 커스텀 화면이지만, 이 지면은
 * 경로+타이틀만 달라 팩토리로 충분하다. `<title>` 이 기획전 이름(동적)이라 고정 문구를 쓴다
 * (`AffiliateEventDetail` 과 같은 판단).
 */
export const EventSegmentDetailScreen = createWebviewScreen({
  screen: 'EventSegmentDetail',
  title: '',
  updateTitle: true,
  showHeader: true,
  rightIcons: [<CartIcon />],
});

/**
 * 어필리에이트(파트너스) 기획전 상세(`/affiliate/events/:eventSno`).
 *
 * 일반 기획전(`EventDetail`)과 다른 지면이라 별도 라우트다. 그쪽은 웹 타이틀을 받아 헤더에
 * 반영하는 커스텀 동작이 있어 개별 파일이지만, 이 화면은 경로+타이틀만 달라 팩토리로 충분하다.
 * SPA 라 서버 `<title>` 이 없어 타이틀은 지면 의미로 정했다.
 */
export const AffiliateEventDetailScreen = createWebviewScreen({
  screen: 'AffiliateEventDetail',
  title: '기획전',
});

/**
 * 오늘출발관(`/today-delivery`). `configs.today_delivery.department_deeplink` 가 여는 지면.
 *
 * 페이지가 자체 타이틀바("오늘출발")를 그려서 showHeader 는 켜지 않는다
 * (`useWebviewUri` 가 붙이는 `global_layout=app` 만 있는 URL 로 실측).
 */
export const TodayDeliveryScreen = createWebviewScreen({
  screen: 'TodayDelivery',
  title: '오늘출발',
});
